import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import type { ItemRenderData } from "@app/types/itemRenderData";
import AwesomeDebouncePromise from "awesome-debounce-promise";
import {
  ItemTypesThatCanBeCompletionActions,
  CompletionActionRenderData,
} from "@app/types/completionActionRenderData";
import {
  extractPersistableItemInstanceData,
  validateItem,
  ValidateItemResult,
} from "@questmate/common";
import isEqual from "react-fast-compare";
import {
  SnackbarContext,
  SnackbarSeverity,
} from "@app/components/snackbar/SnackbarContext";
import { Analytics } from "@app/analytics";
import promisePoller from "promise-poller";
import { useIsFocused } from "@react-navigation/native";
import {
  ItemType,
  JsonObject,
  QuestInstanceDetail,
  ReviewResult,
  RewardInstance,
} from "@questmate/openapi-spec";
import {
  fetchQuestInstance,
  fetchRewardInstance,
  submitQuestInstance,
  updateItemInstance,
} from "@app/util/client/requests/quests";
import { useQuestViewContext } from "@app/quest/QuestViewContext";
import { useRequest, UseRequestResult } from "@app/util/client/requests";
import produce, { Draft } from "immer";
import { useAppSelector } from "@app/store";
import { selectRunViewQuestDataById } from "@app/store/cache/questInstances";
import { useChangeTracker } from "@app/quest/edit/useChangeTracker";

export type OnItemChangeHandler = (
  item: ItemRenderData,
  options?: {
    /**
     * Set to `true` to skip sending a save request.
     */
    skipSave?: boolean;
    /**
     * Set to `true` to avoid calling `kioskInactivityTimeoutManager.reportActivityOccurred()`
     * This allows us to avoid marking a Quest run as dirty when a user has not interacted with the Quest.
     */
    skipActivityReport?: boolean;
  }
) => void;

interface useQuestInstanceData {
  completionActions: CompletionActionRenderData[];
  items: ItemRenderData[];
  submitQuestLoading: boolean;
  onItemChange: OnItemChangeHandler;
  onItemValidate: (itemId: string) => void;
  onSubmit: () => Promise<QuestInstanceDetail>;
  onReview: (reviewResult: ReviewResult) => void;

  SWR: UseRequestResult<QuestInstanceDetail>;
}

export const useQuestInstance = (
  questInstanceId: string
): useQuestInstanceData => {
  const {
    markRecentlySubmittedByUser,
    preSubmitPromiseTracker,
    publicQuestSessionToken,
  } = useQuestViewContext(["RUN"]);
  const isFocused = useIsFocused();

  const SWR = useRequest(
    fetchQuestInstance(questInstanceId, publicQuestSessionToken),
    {
      revalidateOnFocus: false,
    }
  );

  const { mutate: questInstanceMutate } = SWR;

  // Get quest instance data from cache instead of directly from SWR
  const questInstance = useAppSelector((state) => {
    try {
      return selectRunViewQuestDataById(state, questInstanceId);
    } catch (_e) {
      return undefined;
    }
  }, isEqual);

  const snackbarContext = useContext(SnackbarContext);

  // Use changeTracker to manage quest instance data with local changes
  const { useValueWithChanges, getChangeSet, addChange } =
    useChangeTracker(questInstance);

  // Separate state for validation results (UI state, not persisted)
  const [validationResults, setValidationResults] = useState<
    Record<string, ValidateItemResult>
  >({});

  const [submitQuestLoading, setSubmitQuestLoading] = useState(false);

  const persistItemData = useCallback(
    async (
      questInstanceId: string,
      itemPrototypeId: string,
      itemType: ItemType,
      version: number,
      data: JsonObject,
      changeIds?: string[]
    ): Promise<void> => {
      try {
        await updateItemInstance(
          questInstanceId,
          itemPrototypeId,
          {
            version: version,
            data: extractPersistableItemInstanceData(itemType, data),
          },
          publicQuestSessionToken
        );

        // Mark changes as saved on success
        if (changeIds) {
          getChangeSet().markSaved(changeIds);
        }
      } catch (error) {
        // Revert to unsaved on failure
        if (changeIds) {
          getChangeSet().markUnsaved(changeIds);
        }
        throw error;
      }
    },
    [publicQuestSessionToken, getChangeSet]
  );

  const debouncedPersistItemData = useMemo(
    () =>
      AwesomeDebouncePromise(persistItemData, 500, {
        onlyResolvesLast: true,
        key: (...args) => args[1],
      }),
    [persistItemData]
  );

  // Get quest instance data with any local changes applied
  const questInstanceWithChanges = useValueWithChanges();

  const onItemValidate = useCallback(
    (itemId: string) => {
      const currentData = getChangeSet().valueWithChanges;
      const itemInstance = currentData?.itemInstancesById?.[itemId];
      if (!itemInstance || itemInstance.isCompletionAction) {
        return;
      }

      const validateResult = validateItem({
        type: itemInstance.prototype.type,
        required: itemInstance.prototype.required,
        data: itemInstance.data,
      });

      setValidationResults((prev) => ({
        ...prev,
        [itemId]: validateResult,
      }));
    },
    [getChangeSet]
  );

  const onItemChange = useCallback(
    (
      item: ItemRenderData,
      options?: { skipSave?: boolean; skipActivityReport?: boolean }
    ) => {
      const { skipSave = false } = options ?? {};

      addChange((draft) => {
        const itemInstance = draft?.itemInstancesById?.[item.prototype.id];
        if (itemInstance) {
          itemInstance.data = item.data;
          itemInstance.version = item.version;
        }
      });

      // Clear validation result when item changes (will be recalculated when needed)
      setValidationResults((prev) => {
        const updated = { ...prev };
        delete updated[item.prototype.id];
        return updated;
      });

      if (skipSave) {
        return;
      }

      // Mark changes as pending and initiate save
      const changeIds = getChangeSet().getChangesForPath([
        "itemInstancesById",
        item.prototype.id,
        "data",
      ]);
      const pendingIds = getChangeSet().markPending(changeIds);

      debouncedPersistItemData(
        questInstanceId,
        item.prototype.id,
        item.prototype.type,
        item.version,
        item.data,
        pendingIds
      ).catch((e) => {
        // Mark as unsaved on failure
        getChangeSet().markUnsaved(pendingIds);
        console.log("Failed to persist item data", e.response);
      });
    },
    [
      questInstanceId,
      addChange,
      getChangeSet,
      debouncedPersistItemData,
      setValidationResults,
    ]
  );

  const onSubmit = useCallback(async (): Promise<QuestInstanceDetail> => {
    Analytics.trackEvent("Try Submit Quest", {
      questInstanceId: questInstanceId,
    });

    setSubmitQuestLoading(true);

    try {
      // Wait for all preSubmit promises to complete. e.g. file uploads
      // If something is thrown here, just reset the button and allow the user to try again.
      await preSubmitPromiseTracker.allTrackedPromisesAreComplete();
    } catch (e) {
      console.warn(
        "Something requested to stop submission. Fix any issues and try submitting again.",
        e
      );
      setSubmitQuestLoading(false);
      throw new Error(
        "A pre-submission task did not succeed. Stopping submission."
      );
    }

    const validateAllItemsForSubmit = (): boolean => {
      const currentData = getChangeSet().valueWithChanges;
      if (!currentData?.itemInstancesById) {
        return false;
      }

      let allValid = true;

      // Validate all items and update their validation results
      const newValidationResults: Record<string, ValidateItemResult> = {};

      Object.values(currentData.itemInstancesById).forEach((itemInstance) => {
        if (!itemInstance.isCompletionAction && itemInstance.prototype.name) {
          // Only validate named items
          const validateResult = validateItem({
            type: itemInstance.prototype.type,
            required: itemInstance.prototype.required,
            data: itemInstance.data,
          });

          newValidationResults[itemInstance.prototype.id] = validateResult;

          if (!validateResult.valid) {
            allValid = false;
          }
        }
      });

      // Update validation results state
      setValidationResults((prev) => ({ ...prev, ...newValidationResults }));

      return allValid;
    };

    const allValid = validateAllItemsForSubmit();
    if (!allValid) {
      setSubmitQuestLoading(false);
      snackbarContext.sendMessage(
        "Unable to submit Quest. Please fix errors and try again.",
        SnackbarSeverity.WARNING
      );
      return Promise.reject();
    }
    const currentData = getChangeSet().valueWithChanges;
    const itemVersions = Object.entries(
      currentData?.itemInstancesById || {}
    ).map(([itemPrototypeId, itemInstance]) => {
      return {
        id: `${questInstanceId}|${itemPrototypeId}`,
        version: itemInstance.version,
        data: extractPersistableItemInstanceData(
          itemInstance.prototype.type,
          itemInstance.data
        ),
      };
    });
    return submitQuestInstance(
      questInstanceId,
      {
        itemVersions: itemVersions,
      },
      publicQuestSessionToken
    )
      .then((updatedInstanceData) => {
        if (updatedInstanceData) {
          void questInstanceMutate(updatedInstanceData, false);
        }

        setSubmitQuestLoading(false);
        markRecentlySubmittedByUser();
        return updatedInstanceData;
      })
      .catch((error) => {
        console.error("Failed to submit quest!", error);
        snackbarContext.sendMessage(
          "Unable to submit Quest. Please try again.",
          SnackbarSeverity.WARNING
        );
        setSubmitQuestLoading(false);
        throw error;
      });
  }, [
    markRecentlySubmittedByUser,
    preSubmitPromiseTracker,
    publicQuestSessionToken,
    questInstanceId,
    questInstanceMutate,
    getChangeSet,
    snackbarContext,
  ]);

  const onReview = useCallback(
    (reviewResult: ReviewResult) =>
      questInstanceMutate(
        (formInstance) =>
          produce(formInstance, (draft) => {
            draft!.requestingUser.reviewResult = reviewResult;
          }),
        true
      ),
    [questInstanceMutate]
  );

  const pollingRewardsRef = useRef<Record<string, Promise<RewardInstance>>>({});

  const isFocusedRef = useRef(isFocused);
  isFocusedRef.current = isFocused;

  useEffect(() => {
    if (!isFocused) {
      pollingRewardsRef.current = {};
      return;
    }

    const rewardInstances = questInstance?.rewardInstancesById
      ? Object.values(questInstance.rewardInstancesById)
      : [];
    rewardInstances.forEach((reward) => {
      const currentlyPolling = pollingRewardsRef.current[reward.prototypeId];
      if (reward.status === "PENDING" && !currentlyPolling) {
        const poller = createPollerForRewardInstance(
          questInstanceId,
          reward.prototypeId,
          false,
          publicQuestSessionToken
        );

        pollingRewardsRef.current[reward.prototypeId] = poller;

        poller
          .then((reward) => {
            void questInstanceMutate(
              produce((draft: Draft<QuestInstanceDetail | undefined>) => {
                const index = draft!.rewardInstances.findIndex(
                  ({ prototype }) => prototype.id === reward.prototype.id
                );
                draft!.rewardInstances[index] = reward;
              })
            );
          })
          .catch((e) => {
            console.error(
              "Completion action did not complete successfully.",
              e
            );
          });
      }
    });
  }, [
    questInstance?.rewardInstancesById,
    isFocused,
    questInstanceId,
    publicQuestSessionToken,
    questInstanceMutate,
  ]);

  const completionActions = useMemo(() => {
    const completionActionItems = questInstance?.itemInstancesById
      ? Object.values(questInstance.itemInstancesById)
          .filter((item) => item.isCompletionAction)
          .map((item): CompletionActionRenderData => {
            return {
              data: item.data,
              version: item.version,
              prototype: {
                id: item.prototype.id,
                position: item.prototype.position,
                name: item.prototype.name,
                type: item.prototype
                  .type as ItemTypesThatCanBeCompletionActions,
              },
            };
          })
      : [];

    const completionActionRewards = questInstance?.rewardInstancesById
      ? Object.values(questInstance.rewardInstancesById).map((reward) => {
          return {
            status: reward.status,
            data: reward.data,
            prototype: reward.prototype,
          } as CompletionActionRenderData;
        })
      : [];

    return [
      ...(completionActionItems || []),
      ...(completionActionRewards || []),
    ].sort((a, b) => {
      if (a.prototype.position === b.prototype.position) {
        return a.prototype.id.localeCompare(b.prototype.id);
      }
      return a.prototype.position - b.prototype.position;
    });
  }, [questInstance?.rewardInstancesById, questInstance?.itemInstancesById]);

  const items = useMemo(() => {
    if (!questInstanceWithChanges?.itemInstancesById) {
      return [];
    }

    return Object.values(questInstanceWithChanges.itemInstancesById)
      .filter(
        (itemInstance) =>
          !itemInstance.isCompletionAction && !!itemInstance.prototype.name
      )
      .map((itemInstance): ItemRenderData => {
        // Only include validation result if it has been explicitly validated
        const validationResult = validationResults[itemInstance.prototype.id];

        const itemData: ItemRenderData = {
          prototype: itemInstance.prototype,
          version: itemInstance.version,
          data: itemInstance.data,
        };

        // Only attach validateResult if validation has been performed
        if (validationResult) {
          itemData.validateResult = validationResult;
        }

        return itemData;
      })
      .sort((a, b) => a.prototype.position - b.prototype.position);
  }, [questInstanceWithChanges, validationResults]);

  if (!questInstance) {
    return {
      items: [],
      completionActions: [],
      onSubmit: () => {
        throw new Error("onSubmit called before Quest Instance received.");
      },
      onReview: () => {
        throw new Error("onReview called before Quest Instance received.");
      },
      onItemChange: () => {
        throw new Error("onItemChange called before Quest Instance received.");
      },
      onItemValidate: () => {
        throw new Error(
          "onItemValidate called before Quest Instance received."
        );
      },
      submitQuestLoading: false,
      SWR,
    };
  }

  return {
    completionActions,
    items,
    submitQuestLoading,
    onItemChange,
    onItemValidate,
    onSubmit,
    onReview,
    SWR,
  };
};

export const createPollerForRewardInstance = (
  questInstanceId: string,
  rewardPrototypeId: string,
  failFast: boolean,
  publicQuestSessionToken?: string
): Promise<RewardInstance> =>
  promisePoller<RewardInstance>({
    taskFn: () =>
      fetchRewardInstance(
        questInstanceId,
        rewardPrototypeId,
        publicQuestSessionToken
      ).execute(),
    shouldContinue(error: unknown, reward?: RewardInstance): boolean {
      if (error && failFast) {
        return false;
      }
      return reward?.status !== "COMPLETED" && reward?.status !== "FAILED";
    },
    timeout: 5 * 1000,
    retries: 100,
    strategy: "linear-backoff",
    start: 250,
    increment: 250,
    masterTimeout: 3 * 60 * 1000,
  });
